import React from 'react';
import { Card, Result, Button } from 'antd';
import CombatControls from './CombatControls';
import CombatantListContainer from './CombatantListContainer';
import styles from './CombatTracker.module.css';
import { logger } from '../../services/logger.client';
import type { Combatant } from '../../types/types';

interface CombatTrackerProps {
  /** List of combatants in the encounter */
  combatants: Combatant[];
  /** Whether combat is currently active (not in setup mode) */
  isActive: boolean;
  /** Whether combat is in setup mode (adding combatants, setting initiative) */
  setupMode: boolean;
  /** Current combat round number */
  round: number;
  /** Current turn index within the combatants array */
  turn: number;
  /** Whether session data is still loading from the server */
  isLoading: boolean;
  /** Whether loading has exceeded the 10-second timeout threshold */
  loadingTimeout: boolean;
  /** Callback to enter combat setup mode */
  onSetup: () => void;
  /** Callback to roll initiative for all combatants */
  onRollAll: () => void;
  /** Callback to begin combat (transition from setup to active) */
  onBegin: () => void;
  /** Callback to advance to the next combatant's turn */
  onNextTurn: () => void;
  /** Callback to end the current combat encounter */
  onEnd: () => void;
  /** Callback to perform a long rest (heal all combatants) */
  onLongRest: () => void;
}

/**
 * The main presenter component for the combat tracker.
 *
 * Pure presenter (ADR-034): receives all combat state and action callbacks
 * as props from CombatTrackerContainer. ZERO store access.
 *
 * Handles three render states:
 * 1. Loading timeout - shows error with reload button
 * 2. Loading - shows skeleton card
 * 3. Normal - shows combat controls and combatant list
 */
const CombatTracker: React.FC<CombatTrackerProps> = ({
  combatants,
  isActive,
  setupMode,
  round,
  turn,
  isLoading,
  loadingTimeout,
  onSetup,
  onRollAll,
  onBegin,
  onNextTurn,
  onEnd,
  onLongRest,
}) => {
  // Show loading timeout error if loading takes too long
  if (isLoading && loadingTimeout) {
    logger.warn(
      { decision: 'showTimeoutError', isLoading, loadingTimeout },
      'CombatTracker: Showing loading timeout error UI'
    );
    return (
      <Card title="Combat Tracker">
        <Result
          status="warning"
          title="Loading Timeout"
          subTitle="Combat data is taking too long to load. This might indicate a problem with the server or your connection."
          extra={
            <Button type="primary" onClick={() => window.location.reload()}>
              Reload Page
            </Button>
          }
        />
      </Card>
    );
  }

  // Show loading skeleton while session data is being fetched
  if (isLoading) {
    logger.info(
      { decision: 'showLoading', isLoading, loadingTimeout },
      'CombatTracker: Showing loading UI'
    );
    return <Card title="Combat Tracker" loading />;
  }

  logger.debug(
    { decision: 'showNormalUI', isLoading, loadingTimeout, combatantCount: combatants.length },
    'CombatTracker: Showing normal UI'
  );

  return (
    <Card
      title="Combat Tracker"
      className={styles.card}
    >
      <CombatControls
        isActive={isActive}
        setupMode={setupMode}
        round={round}
        turn={turn}
        onSetup={onSetup}
        onRollAll={onRollAll}
        onBegin={onBegin}
        onNextTurn={onNextTurn}
        onEnd={onEnd}
        onLongRest={onLongRest}
      />
      <CombatantListContainer
        combatants={combatants}
        isActive={isActive}
        setupMode={setupMode}
        turn={turn}
      />
    </Card>
  );
};

export default CombatTracker;